import React, { useContext, useEffect, useState } from "react";

import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GroupIcon from "@mui/icons-material/Group";
import HelpIcon from "@mui/icons-material/Help";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
  Button,
  Grid,
  LinearProgress,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useHistory, useParams } from "react-router-dom";
import mainStyles from "styles";

import {
  GetTemplateAccessRulesResponse,
  TemplateAccessRule,
  TemplateLockStatus,
  TemplatePermission,
  TemplateRecipient,
  TemplateRecipientType,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { getTemplateAccessRules } from "api/endpoints/templates-inspections";
import { callOptionsHeaders } from "api/helpers";
import { SnackbarContext } from "context/snackbar-context";

interface TemplateUrlParamType {
  templateID: string;
  regionCode: string;
}

const TemplateAccess = () => {
  const history = useHistory();
  const { templateID, regionCode } = useParams<TemplateUrlParamType>();
  const [templateInfo, setTemplateInfo] =
    useState<GetTemplateAccessRulesResponse>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { showError } = useContext(SnackbarContext);

  const classes = mainStyles();

  const displayRecipientTypeIcon = (type: TemplateRecipientType) => {
    switch (type) {
      case TemplateRecipientType.ORG:
        return <AccountBalanceIcon fontSize="small" />;
      case TemplateRecipientType.USER:
        return <PersonIcon fontSize="small" />;
      case TemplateRecipientType.GROUP:
        return <GroupIcon fontSize="small" />;
      case TemplateRecipientType.SITE:
        return <LocalOfferIcon fontSize="small" />;
      case TemplateRecipientType.EXTERNAL_USER:
        return <PersonAddIcon fontSize="small" />;
      case TemplateRecipientType.INVITED_USER:
        return <PersonAddIcon fontSize="small" color="disabled" />;
      default:
        return <HelpIcon fontSize="small" color="error" />;
    }
  };

  const displayPermissionString = (
    isAutoshareRule: boolean,
    permission: TemplatePermission,
  ) => {
    const viewName = isAutoshareRule ? "View" : "Conduct";

    switch (permission) {
      case TemplatePermission.OWNER:
        return "Template owner";
      case TemplatePermission.VIEW:
        return `${viewName}`;
      case TemplatePermission.EDIT:
        return `${viewName}, edit`;
      case TemplatePermission.DELETE:
        return `${viewName}, edit, delete`;
      default:
        return "Unknown";
    }
  };

  const displayTemplateLockStatus = (lockStatus?: TemplateLockStatus) => {
    switch (lockStatus) {
      case TemplateLockStatus.UNLOCKED:
        return "Not locked";
      case TemplateLockStatus.LOCKED_FROM_SHARED_LIBRARY:
        return "Locked from shared library";
      case TemplateLockStatus.LOCKED_FROM_GOLDEN_TEMPLATE:
        return "Locked from golden template";
      default:
        return "Unknown";
    }
  };

  const displayRecipientRow = (
    isAutoshareRule: boolean,
    recipient: TemplateRecipient,
  ) => {
    const displayRecipientName = () => {
      switch (recipient.type) {
        case TemplateRecipientType.ORG:
          return (
            <MuiLink
              component={RouterLink}
              to={`/organisation/${regionCode}/${recipient.id}`}
              sx={classes.coloredLinkStyle}
            >
              <Typography>{`All users (${recipient.name})`}</Typography>
            </MuiLink>
          );
        case TemplateRecipientType.GROUP:
          return (
            <MuiLink
              component={RouterLink}
              to={`/organisation/${regionCode}/${recipient.id}`}
              sx={classes.coloredLinkStyle}
            >
              <Typography>{`${recipient.name}`}</Typography>
            </MuiLink>
          );
        case TemplateRecipientType.INVITED_USER:
          return <Typography color="grey">{recipient.name}</Typography>;
        case TemplateRecipientType.SITE:
          if (isAutoshareRule) {
            return (
              <Typography fontStyle="italic">
                All members of selected Site
              </Typography>
            );
          } else {
            return <Typography>{recipient.name}</Typography>;
          }
        default:
          return <Typography>{recipient.name}</Typography>;
      }
    };

    return (
      <Stack alignItems="center" direction="row" gap={2}>
        {displayRecipientTypeIcon(recipient.type)}
        {displayRecipientName()}
      </Stack>
    );
  };

  const displayIntersectionRecipientRow = (
    isAutoshareRule: boolean,
    recipient: TemplateRecipient,
  ) => {
    return (
      <Stack>
        <Stack alignItems="center" direction="row" gap={2}>
          <LocalOfferIcon fontSize="small" />
          <Typography fontStyle="italic">
            {`Members of ${
              isAutoshareRule ? "selected Site" : recipient.name
            } also in`}
          </Typography>
        </Stack>
        <Stack alignItems="center" direction="row" gap={2} paddingLeft={4}>
          <GroupIcon fontSize="small" color="disabled" />
          <MuiLink
            component={RouterLink}
            to={`/organisation/${regionCode}/${recipient.additionalId}`}
            sx={classes.coloredLinkStyle}
          >
            <Typography>{recipient.additionalName}</Typography>
          </MuiLink>
        </Stack>
      </Stack>
    );
  };

  const displayTemplateAccessRuleRow = (row: TemplateAccessRule) => {
    if (!row || !row.recipient) {
      return null;
    }

    return (
      <TableRow>
        <TableCell sx={{ alignItems: "center" }}>
          {row.recipient.type === TemplateRecipientType.INTERSECTION
            ? displayIntersectionRecipientRow(false, row.recipient)
            : displayRecipientRow(false, row.recipient)}
        </TableCell>
        <TableCell>
          <Typography>
            {displayPermissionString(false, row?.permission)}
          </Typography>
        </TableCell>
        <TableCell>
          {row.autoshares.map((autoshare) => {
            if (!autoshare.recipient) {
              return null;
            }
            if (
              autoshare.recipient.type === TemplateRecipientType.INTERSECTION
            ) {
              return displayIntersectionRecipientRow(true, autoshare.recipient);
            }
            return displayRecipientRow(true, autoshare.recipient);
          })}
        </TableCell>
        <TableCell>
          {row.autoshares.map((autoshare) => (
            <>
              <Typography key={autoshare.recipient?.name}>
                {displayPermissionString(true, autoshare.permission)}
              </Typography>
              {autoshare.recipient?.type ===
                TemplateRecipientType.INTERSECTION && <br />}
            </>
          ))}
        </TableCell>
      </TableRow>
    );
  };

  const getTemplateInfo = () => {
    setIsLoading(true);
    getTemplateAccessRules(
      { templateID },
      callOptionsHeaders(regionCode, templateID),
    )
      .then((resp) => setTemplateInfo(resp))
      .catch(showError)
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    getTemplateInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateID]);

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Grid container alignContent="space-between" alignItems="center">
            <Button
              startIcon={<ArrowBackIcon />}
              color="primary"
              data-anchor="template-access-back-btn"
              data-testid="template-access-back-btn"
              onClick={() => history.goBack()}
            >
              Back
            </Button>
            <Grid item xs={12}>
              <Typography
                variant="h5"
                color="textPrimary"
                gutterBottom
                data-testid="title"
              >
                {!isLoading && `${templateInfo?.templateName} - `} Template
                Access Rules
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          justifyContent="flex-end"
          data-testid="template-lock-status"
        >
          <Typography align="right">
            {`Template edit lock: ${displayTemplateLockStatus(
              templateInfo?.lockStatus,
            )}`}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <b>Template available to</b>
                </TableCell>
                <TableCell>
                  <b>Access level</b>
                </TableCell>
                <TableCell>
                  <b>Inspection results available to</b>
                </TableCell>
                <TableCell>
                  <b>Access level</b>
                </TableCell>
              </TableRow>
            </TableHead>
            {!isLoading ? (
              <TableBody>
                {templateInfo?.rules
                  .sort((rule) =>
                    rule.permission === TemplatePermission.OWNER ? -1 : 1,
                  )
                  .map((rule) => displayTemplateAccessRuleRow(rule))}
              </TableBody>
            ) : (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={4}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </Grid>
      </Grid>
    </>
  );
};

export default TemplateAccess;
